/**
 * Connection Store — socket lifecycle and connection state.
 *
 * Single-responsibility: manages the socket instance and connection state.
 * Task-level mutations from CONNECTION_STATE are handled by taskLifecycleHandlers.
 *
 * Replaces: useSocketConnection.ts (289 lines) + SocketConnectionContext.tsx (28 lines)
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { io, type Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import type { ConnectionState } from "./types";
import { registerSocketAccessors, getSelectedTaskId } from "./socketService";
import {
  deriveLegacyStateFromPresence,
  usePresenceStore,
} from "./presenceStore";
import { useJobStore } from "./jobStore";
import { useBrowserStore } from "./browserStore";
import { isDesktopShell } from "@/lib/desktop-shell";
import { supabase } from "@/lib/supabase/client";
import { onAccountUnblocked, signalAccountBlocked } from "@/lib/account-block";
import {
  noteInboundPacket,
  noteRtt,
  noteHeartbeatMiss,
  setBrowserOffline,
  setSocketConnected,
  startHealthEvaluator,
  stopHealthEvaluator,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_ACK_TIMEOUT_MS,
  resetHealthEvidence,
  useConnectionHealthStore,
} from "./connectionHealthStore";
import { markReconnectAdopt } from "./handlers/syncCoordination";
import { recheckLink } from "./linkRecheck";
import { posthog } from "@/lib/posthog";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

// Throttle for session refreshes driven by AUTH_TOKEN_EXPIRED rejections —
// one refresh per cooldown, never a refresh loop (the #2102 resync idiom).
const AUTH_REFRESH_COOLDOWN_MS = 30_000;
let lastAuthRefreshAt: number | null = null;

/**
 * Pure throttle seam: a refresh is due when none has been attempted yet, or
 * the cooldown has fully elapsed since the last one.
 */
export function resyncDue(
  lastMs: number | null,
  cooldownMs: number,
  nowMs: number
): boolean {
  return lastMs === null || nowMs - lastMs >= cooldownMs;
}

export interface WorkspaceSyncToast {
  visible: boolean;
  status: "syncing" | "success" | "error";
  message: string;
}

/**
 * Deploy-updating transport state: the calm gold capsule.
 *
 * A deploy is EXPECTED maintenance, and it should be invisible: Railway only
 * SIGTERMs the old instance after the successor is live, and the old server
 * tells its own tabs before it lets them go (server:handover, or the earlier
 * server:shutting_down notice). A disconnect inside that hand-off reconnects
 * at once and shows nothing. Only a link still down HANDOVER_QUIET_MS after
 * the drop raises this capsule. The TTL bounds the claim: past it, the state
 * degrades to the honest offline UX so an update capsule can never dress up a
 * real outage.
 */
export interface DeployUpdating {
  /** Epoch ms when the server's notice arrived. */
  since: number;
  /** How long "updating" may legitimately persist before degrading. */
  ttlMs: number;
}

/**
 * A deploy hand-off this tab has been told about. Internal and never
 * rendered: it only decides that a drop is the deploy moving this tab, so
 * the drop reconnects at once and stays quiet.
 */
interface Handover {
  /** What told us: the old server's server:handover, or its earlier
   *  server:shutting_down notice (sent while it may still be serving a turn). */
  signal: "handover" | "shutting_down";
  /** Epoch ms the notice arrived. */
  since: number;
  ttlMs: number;
  /** Epoch ms the link dropped inside the window; null while still connected. */
  shedAt: number | null;
}

/** A hand-off shows nothing for this long; a link still down after it raises the capsule. */
export const HANDOVER_QUIET_MS = 3_000;
/** The successor is already live when the old server lets go, so only the
 *  redial is left: past this, a link still down is an outage, not an update. */
export const HANDOVER_WINDOW_MS = 60_000;
/** Client telemetry: handover received → reconnected (with ms), and still down at HANDOVER_QUIET_MS. */
const SOCKET_HANDOVER_EVENT = "socket:handover";
const SOCKET_HANDOVER_SLOW_EVENT = "socket:handover_slow";

/** Payload of server:shutting_down (raw-string event, additive fields). */
interface ServerShuttingDownPayload {
  reason?: string;
  timestamp?: number;
  /** Railway's drain window (RAILWAY_DEPLOYMENT_DRAINING_SECONDS) as the server sees it. */
  drainingSeconds?: number;
}

/** Fallback drain window when the payload predates the field (360s staging canon). */
const DEPLOY_DRAIN_FALLBACK_S = 360;
/** Successor boot + reconverge margin on top of the drain window. */
const DEPLOY_RECONVERGE_MARGIN_MS = 60_000;
/** How long the "Updated" resolve tick stays before dissolving. */
const DEPLOY_UPDATED_TICK_MS = 2_000;

/** Client TTL ceiling from the wire's drain window (exported for tests). */
export function deployUpdatingTtlMs(
  drainingSeconds: number | undefined
): number {
  const drainS =
    typeof drainingSeconds === "number" &&
    Number.isFinite(drainingSeconds) &&
    drainingSeconds > 0
      ? drainingSeconds
      : DEPLOY_DRAIN_FALLBACK_S;
  return drainS * 1000 + DEPLOY_RECONVERGE_MARGIN_MS;
}

interface ConnectionStore {
  // State
  connectionState: ConnectionState;
  activeTaskCount: number;
  activityReasons: string[];
  isSocketConnected: boolean;
  isSocketReconnecting: boolean;
  /** Structured code from the last connect_error (server ExtendedError.data),
   *  e.g. 'AUTH_TOKEN_EXPIRED' — null once a connect succeeds. Lets dead-end
   *  UI say "sign in again" instead of a generic can't-reach-server. */
  lastConnectErrorCode: string | null;
  workspaceSyncToast: WorkspaceSyncToast;
  /** Non-null once a deploy hand-off has kept the link down past
   *  HANDOVER_QUIET_MS (capsule shows, sends pause while disconnected). */
  deployUpdating: DeployUpdating | null;
  /** Brief post-reconnect "Updated" resolve tick (capsule tick, then dissolves). */
  deployUpdatedTick: boolean;
  capabilities: {
    hasBrowserStreaming: boolean;
  };

  // Internal (not for rendering — use getSocket() to access)
  _socket: Socket | null;
  _cleanupFns: (() => void)[];
  _syncToastTimer: ReturnType<typeof setTimeout> | null;
  _deployTtlTimer: ReturnType<typeof setTimeout> | null;
  /** The retry after a server-side close — socket.io will not try on its own after one. */
  _serverRetryTimer: ReturnType<typeof setTimeout> | null;
  _serverRetries: number;
  _deployTickTimer: ReturnType<typeof setTimeout> | null;
  /** The deploy hand-off in progress, if any (its TTL is _deployTtlTimer). */
  _handover: Handover | null;
  /** Raises the capsule if the hand-off's drop outlives HANDOVER_QUIET_MS. */
  _handoverSlowTimer: ReturnType<typeof setTimeout> | null;

  // Actions
  getSocket: () => Socket | null;
  initSocket: (authToken: string) => void;
  destroySocket: () => void;
  ensureConnected: () => void;
  /** Ask the server for the truth again: sandbox state + the selected task's messages. */
  resync: () => void;
  /** Drop the socket and dial again — for a link that is up but deaf. */
  bounce: () => void;
  setConnectionState: (state: ConnectionState) => void;
  setCapabilities: (caps: { hasBrowserStreaming: boolean }) => void;
  dismissSyncToast: () => void;
}

export const useConnectionStore = create<ConnectionStore>()(
  devtools(
    (set, get) => ({
      // Start as 'loading' — we don't know sandbox state until the server tells us.
      // Only CONNECTION_STATE events from the server set the real sandbox state.
      connectionState: { status: "loading" },
      activeTaskCount: 0,
      activityReasons: [],
      isSocketConnected: false,
      isSocketReconnecting: false,
      lastConnectErrorCode: null,
      workspaceSyncToast: { visible: false, status: "syncing", message: "" },
      deployUpdating: null,
      deployUpdatedTick: false,
      capabilities: { hasBrowserStreaming: false },
      _socket: null,
      _cleanupFns: [],
      _syncToastTimer: null,
      _deployTtlTimer: null,
      _serverRetryTimer: null,
      _serverRetries: 0,
      _deployTickTimer: null,
      _handover: null,
      _handoverSlowTimer: null,

      getSocket: () => get()._socket,

      setConnectionState: (newState: ConnectionState) => {
        const current = get().connectionState;
        if (
          newState.status === current.status &&
          newState.error === current.error
        )
          return;
        // No guards — server state machine owns all transitions.
        set({ connectionState: newState });
      },

      setCapabilities: (caps) => set({ capabilities: caps }),

      dismissSyncToast: () => {
        const timer = get()._syncToastTimer;
        if (timer) clearTimeout(timer);
        set({
          workspaceSyncToast: {
            visible: false,
            status: "syncing",
            message: "",
          },
          _syncToastTimer: null,
        });
      },

      // Reconnect socket if it's disconnected (e.g. after exhausted retries or network recovery).
      // Pure transport concern — no sandbox knowledge.
      resync: () => {
        const { _socket } = get();
        if (!_socket?.connected) {
          get().ensureConnected();
          return;
        }
        _socket.emit(EVENTS.CONNECTION_STATE_QUERY);
        const taskId = getSelectedTaskId();
        if (taskId) {
          markReconnectAdopt(taskId);
          _socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
        }
      },

      bounce: () => {
        const { _socket } = get();
        if (!_socket) return;
        set({ isSocketReconnecting: true });
        _socket.disconnect();
        _socket.io.reconnection(true);
        _socket.connect();
      },

      ensureConnected: () => {
        const { _socket } = get();
        if (_socket && !_socket.connected) {
          // A deploy hand-off is already re-dialing and says nothing until
          // HANDOVER_QUIET_MS; a send in that gap must not flash "reconnecting".
          if (!get()._handover) set({ isSocketReconnecting: true });
          _socket.io.reconnection(true);
          _socket.connect();
        }
      },

      initSocket: (authToken: string) => {
        // Destroy previous socket if any
        get().destroySocket();

        // The last token that was live (or the one we were initialized with) —
        // only used when the live session read below fails.
        let lastKnownToken = authToken;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const socket = io(SERVER_URL, {
          // FUNCTION form: socket.io resolves auth per connection attempt, so a
          // reconnect after token expiry re-mints from the CURRENT Supabase
          // session instead of replaying a dead credential captured at init
          // (prior art: apps/extension/panel/socket.ts). The 07-30 white screen
          // was the static-object form retrying an expired JWT forever.
          auth: (cb) => {
            supabase.auth
              .getSession()
              .then(({ data }) => {
                if (data.session?.access_token)
                  lastKnownToken = data.session.access_token;
                cb({ token: lastKnownToken, timezone });
              })
              .catch(() => cb({ token: lastKnownToken, timezone }));
          },
          transports: ["websocket"],
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 30000,
        });

        const { setConnectionState } = get();

        // Online/Offline — only manage socket reconnection, don't override sandbox state.
        // Server will send CONNECTION_STATE once reconnected.
        const redial = () => {
          if (!get()._handover) set({ isSocketReconnecting: true });
          socket.io.reconnection(true);
          socket.connect();
        };
        // CONNECT_CATCHUP_V1 (linkRecheck.ts): a page that comes back also proves a socket that
        // says it is up; off, only a socket that knows it is down re-dials, as before.
        const recheck = () =>
          recheckLink({
            socket,
            reconnect: redial,
            bounce: () => get().bounce(),
          });
        const handleOnline = () => {
          setBrowserOffline(false);
          recheck();
        };
        const handleOffline = () => {
          setBrowserOffline(true);
          // Only set disconnected if we haven't received sandbox state yet
          // (avoids overriding valid sandbox state during brief network blips)
          setConnectionState({ status: "offline", error: "Network offline" });
        };
        // Tab visibility — reconnect when user returns to a suspended tab.
        // Browser suspends WebSocket during background, so ping timeout may have fired.
        const handleVisibilityChange = () => {
          if (document.visibilityState === "visible") recheck();
        };
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Reconnection — surface "trying to connect" in the UI instead of silent "Offline"
        const handleReconnectAttempt = () => {
          // Inside a deploy hand-off the quiet timer decides when to say so.
          if (!get()._handover) set({ isSocketReconnecting: true });
        };
        const handleReconnectFailed = () => {
          set({ isSocketReconnecting: false });
          // All retry attempts exhausted — now show the error
          setConnectionState({
            status: "error",
            error: "Unable to connect to server",
          });
        };

        // Health rail evidence (connection-truth): the engine emits 'packet' on
        // EVERY inbound frame (pongs included), so silence — not error events — is
        // the outage signal. The engine object is REPLACED on each reconnect, so
        // the listener re-attaches inside handleConnect; the ref guards dupes.
        let packetEngine: { on: (ev: string, fn: () => void) => void } | null =
          null;
        const handleEnginePacket = () => noteInboundPacket();
        // Heartbeat RTT probe — visible tab only; a slow ack is the "slow
        // internet" evidence the packet clock alone can't give.
        const heartbeatTimer = setInterval(() => {
          if (
            typeof document !== "undefined" &&
            document.visibilityState !== "visible"
          )
            return;
          if (!socket.connected) return;
          const t0 = Date.now();
          socket
            .timeout(HEARTBEAT_ACK_TIMEOUT_MS)
            .emit(EVENTS.CONNECTION_HEARTBEAT, {}, (err: unknown) => {
              if (err) noteHeartbeatMiss();
              else noteRtt(Date.now() - t0);
            });
        }, HEARTBEAT_INTERVAL_MS);
        startHealthEvaluator();
        // The health rail is the truth about the link, the socket's own belief is
        // not: a laptop back from sleep or a half-open TCP keeps `connected` true
        // while nothing arrives. "Down" with the socket still up is a deaf link,
        // and the one remedy is to re-dial (a stuck "reconnecting" / "standby"
        // was this, 08-30). Not connected: another rail re-dials, never this
        // one: socket.io's own retry after a transport drop, the backoff in
        // handleDisconnect after a server close, and the unblock signal (bottom
        // of this file) after an ACCOUNT_BLOCKED refusal, which socket.io never
        // retries.
        const unsubscribeHealth = useConnectionHealthStore.subscribe(
          (now, before) => {
            if (
              now.health === "down" &&
              before.health !== "down" &&
              socket.connected
            )
              get().bounce();
          }
        );

        // Connect
        const handleConnect = () => {
          setSocketConnected(true);
          const retry = get();
          if (retry._serverRetryTimer) clearTimeout(retry._serverRetryTimer);
          if (retry._serverRetryTimer || retry._serverRetries)
            set({ _serverRetryTimer: null, _serverRetries: 0 });
          const engine = socket.io.engine as unknown as
            { on: (ev: string, fn: () => void) => void } | undefined;
          if (engine && engine !== packetEngine) {
            engine.on("packet", handleEnginePacket);
            packetEngine = engine;
          }
          // Any connect lands on the live server, so a deploy hand-off is over.
          // A quiet one leaves no trace; one that raised the capsule resolves
          // into the brief "Updated" tick below.
          const {
            _handover: handover,
            _deployTtlTimer,
            _handoverSlowTimer,
          } = get();
          if (handover) {
            if (_deployTtlTimer) clearTimeout(_deployTtlTimer);
            if (_handoverSlowTimer) clearTimeout(_handoverSlowTimer);
            set({
              _handover: null,
              _handoverSlowTimer: null,
              _deployTtlTimer: null,
            });
            if (handover.shedAt !== null) {
              const ms = Date.now() - handover.shedAt;
              posthog.capture(SOCKET_HANDOVER_EVENT, {
                signal: handover.signal,
                ms,
              });
              console.info(
                `[Deploy] handed over (${handover.signal}) in ${ms}ms`
              );
            }
          }
          const deploy = get();
          if (deploy.deployUpdating) {
            const tickTimer = setTimeout(() => {
              set({ deployUpdatedTick: false, _deployTickTimer: null });
            }, DEPLOY_UPDATED_TICK_MS);
            console.info(
              `[Deploy] updated — window resolved in ${
                Date.now() - deploy.deployUpdating.since
              }ms`
            );
            set({
              deployUpdating: null,
              deployUpdatedTick: true,
              _deployTickTimer: tickTimer,
            });
          }

          const prev = get().connectionState;
          set({
            isSocketConnected: true,
            isSocketReconnecting: false,
            lastConnectErrorCode: null,
            // On reconnect, keep previous server-sent state — don't flash "loading".
            // Only clear stale transport errors; server's AUTO-PUSH will send real state.
            connectionState:
              prev.status === "error" ? { status: "loading" } : prev,
          });

          socket.emit(EVENTS.CONNECTION_STATE_QUERY);

          const taskId = getSelectedTaskId();
          if (taskId) {
            // Connection-truth: this sync is reconnect-born — mark it so the sync
            // handler adopts the server's mid-turn materialization instead of
            // dropping a both-processing snapshot (the blind-window repaint).
            markReconnectAdopt(taskId);
            socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId });
            // Re-sync the staged queue too: a MESSAGE_PICKED_UP missed during the
            // disconnect (or an agent-recycle that cleared the in-memory queue) is
            // only reconciled by a fresh STAGING_SYNC. The StagedQueue mount effect
            // does NOT re-fire on a silent reconnect, so drive it here to avoid
            // ghost cards lingering until the next task-switch (spec §5.3 / §8).
            socket.emit(EVENTS.STAGING_SYNC_REQUEST, { taskId });
            // Backfill any JOB_READY events missed while socket was disconnected.
            useJobStore.getState().seedFromSnapshot(taskId);
            // Remote lane only: server pane demand is a
            // per-socket registry, so a fresh socket — silent reconnect OR a server
            // restart that wiped the registry — starts with zero demand. If this
            // client is still showing the pane for the selected task (the same
            // compound gate StoryView mounts the Window on), re-assert demand so
            // it survives by convergence, not persistence.
            if (!isDesktopShell()) {
              const browser = useBrowserStore.getState();
              if (
                browser.hasBrowserSession &&
                browser.panelOpenTaskIds.has(taskId)
              ) {
                socket.emit(EVENTS.BROWSER_OPEN_SESSION, {});
              }
            }
          }

          // Subscribe to PRESENCE_UPDATE and mirror into connectionStore's legacy
          // fields so all downstream consumers (useAgentDisplayStatus, useActiveTaskCount,
          // useIsSandboxUpgrading, ConnectionPopover, ConversationView retry CTA,
          // input pre-warm gate, taskStore.retryConnection) keep working without
          // per-consumer migration. The legacy DisplayStatus surface stays via the
          // deriveLegacyStateFromPresence shim until a future UI refactor.
          usePresenceStore.getState()._subscribe();

          // Seed once from current presence (in case PRESENCE_UPDATE already fired
          // before this bridge attached), then subscribe for updates.
          const seedLegacy = () => {
            const presence = usePresenceStore.getState().presence;
            // The pane's RESOLVED state is the second input: 'browsing' is derived
            // from a LIVE pane (pixels streaming), never from a marker. See
            // deriveLegacyStateFromPresence.
            const paneState =
              useBrowserStore.getState().viewSnapshot?.state ?? "hidden";
            const legacy = deriveLegacyStateFromPresence(presence, paneState);
            set({
              connectionState: { status: legacy.status, error: legacy.error },
              activeTaskCount: legacy.activeTaskCount,
              activityReasons: legacy.activityReasons,
            });
          };
          seedLegacy();

          // Vanilla Zustand subscribe — fires on every store change. We filter
          // to presence changes only, then project through the shim.
          const unsubPresence = usePresenceStore.subscribe((state, prev) => {
            if (state.presence !== prev.presence) {
              seedLegacy();
            }
          });
          // The badge has TWO inputs now — re-derive when the pane's resolved state
          // changes too (live ⇄ waking/suspended), or 'browsing' would lag until the
          // next unrelated presence beat.
          const unsubPane = useBrowserStore.subscribe((state, prev) => {
            if (state.viewSnapshot?.state !== prev.viewSnapshot?.state) {
              seedLegacy();
            }
          });
          // Merge into _cleanupFns so destroySocket tears it down and we don't
          // leak a zombie subscription pushing updates after the socket closes.
          set((s) => ({
            _cleanupFns: [...s._cleanupFns, unsubPresence, unsubPane],
          }));
        };

        // Disconnect — handle socket lifecycle, but don't override sandbox state for
        // transient disconnects. The sandbox is still running even if our WebSocket drops.
        const handleDisconnect = (reason: string) => {
          setSocketConnected(false);
          set({ isSocketConnected: false });

          // The browser pane's view snapshot describes a box we can no longer
          // hear from: anything the server decided while we were away — a
          // stand-down, a hibernate — was said to a dead socket. Mark it stale
          // so a tab coming back (a closed lid opens: `visible` fires before the
          // reconnect's replay lands) does not dial yesterday's `live` creds
          // into a hibernated preview and thaw the box for nothing. Cleared by
          // the first snapshot after reconnect. No-op with PANE_VISIBILITY_V1
          // off — the store action itself is flag-gated.
          useBrowserStore.getState().markSnapshotStale();

          // Deploy hand-off: the old server is handing this tab to the live
          // successor (Railway SIGTERMs the old instance only after the new one
          // is active). Never the error-and-retry branch below: reconnect at once
          // and say nothing; the quiet timer raises the capsule only if the link
          // is still down after HANDOVER_QUIET_MS. A client-side close is ours
          // after a server:handover (the redial follows at once); after only the
          // shutting_down notice it is a real one and falls through.
          const handover = get()._handover;
          if (
            handover &&
            (reason !== "io client disconnect" ||
              handover.signal === "handover")
          ) {
            if (handover.shedAt === null) {
              const slowTimer = setTimeout(
                handleHandoverSlow,
                HANDOVER_QUIET_MS
              );
              set({
                _handover: { ...handover, shedAt: Date.now() },
                _handoverSlowTimer: slowTimer,
              });
            }
            // socket.io never retries an 'io server disconnect' on its own, so
            // dial now. Any other drop it retries itself; a connect() from
            // inside its close would leave its manager believing it is still
            // reconnecting, and every later drop would then go unretried.
            if (reason === "io server disconnect") {
              console.info(
                `[Deploy] socket shed (${reason}) — reconnecting to successor now`
              );
              socket.io.reconnection(true);
              socket.connect();
            }
            return;
          }

          // Only show 'disconnected' if the socket won't auto-reconnect
          // (user-initiated disconnect or all retries exhausted).
          // For transient disconnects, Socket.io auto-reconnects and the server
          // will send CONNECTION_STATE with the real sandbox state.
          if (reason === "io client disconnect") {
            setConnectionState({ status: "offline", error: reason });
            return;
          }
          if (reason === "io server disconnect") {
            // The server closed the socket on us. Socket.io will NOT try again on
            // its own after a server-side close — and a link that never comes back
            // is a stuck screen (founder, 08-30: "the thing should not get stuck").
            // So we try, with backoff: 2s, 4s, … capped at 30s, until we are back.
            const retries = get()._serverRetries;
            const delay = Math.min(2000 * 2 ** retries, 30_000);
            if (get()._serverRetryTimer) clearTimeout(get()._serverRetryTimer!);
            set({ isSocketReconnecting: true, _serverRetries: retries + 1 });
            setConnectionState({ status: "error", error: reason });
            const timer = setTimeout(() => {
              set({ _serverRetryTimer: null });
              if (socket.connected) return;
              socket.io.reconnection(true);
              socket.connect();
            }, delay);
            set({ _serverRetryTimer: timer });
            return;
          }
          // For transport errors / ping timeouts, keep current state — reconnect is in progress
        };

        const handleConnectError = (error: Error) => {
          console.error("[Socket] Connection error:", error);
          // Structured rejection code minted by the server's socket-auth
          // middleware (ExtendedError.data) — the only field we key on; never
          // string-match the message text.
          const code =
            (error as Error & { data?: { code?: string } }).data?.code ?? null;
          set({ lastConnectErrorCode: code });
          if (code === "ACCOUNT_BLOCKED") {
            // An admin blocked this account: the gate takes over. A refresh
            // cannot help, so none is attempted. socket.io never retries a
            // middleware refusal; the unblock signal re-dials.
            // A deploy hand-off in flight ends here too: the suspended screen
            // owns the page, and no "Updating" capsule may rise beside it.
            const s = get();
            if (s._handover) {
              if (s._deployTtlTimer) clearTimeout(s._deployTtlTimer);
              if (s._handoverSlowTimer) clearTimeout(s._handoverSlowTimer);
              set({
                _handover: null,
                _handoverSlowTimer: null,
                _deployTtlTimer: null,
                deployUpdating: null,
              });
            }
            signalAccountBlocked();
          }
          if (code === "AUTH_TOKEN_EXPIRED") {
            // Re-mint at most once per cooldown; the function-form auth above
            // hands the refreshed token to socket.io's own retry. On refresh
            // failure do NOT loop — Supabase fires SIGNED_OUT and AuthContext
            // bounces protected routes to /login.
            const now = Date.now();
            if (resyncDue(lastAuthRefreshAt, AUTH_REFRESH_COOLDOWN_MS, now)) {
              lastAuthRefreshAt = now;
              void supabase.auth.refreshSession().catch((err) => {
                console.warn(
                  "[Socket] refreshSession after AUTH_TOKEN_EXPIRED failed",
                  err
                );
              });
            }
          }
          // During reconnection, connect_error fires for each failed attempt (e.g. stale JWT).
          // Don't flash 'error' — wait for success or handleReconnectFailed.
          // A deploy hand-off is a reconnection too, quiet until its timer says otherwise.
          if (!get().isSocketReconnecting && !get()._handover) {
            setConnectionState({ status: "error", error: error.message });
          }
        };

        // Arm (or re-arm, when a second notice overlaps the first) the hand-off
        // window. Silent by design: the tab is connected and working, so there
        // is nothing to show until a drop outlives HANDOVER_QUIET_MS.
        const armHandover = (signal: Handover["signal"], ttlMs: number) => {
          const s = get();
          if (s._deployTtlTimer) clearTimeout(s._deployTtlTimer);
          if (s._handoverSlowTimer) clearTimeout(s._handoverSlowTimer);
          const ttlTimer = setTimeout(() => {
            // Still not back past the whole legitimate window — stop claiming "update".
            const s = get();
            if (!s._handover) return;
            if (s._handoverSlowTimer) clearTimeout(s._handoverSlowTimer);
            set({
              _handover: null,
              _handoverSlowTimer: null,
              deployUpdating: null,
              deployUpdatedTick: false,
              _deployTtlTimer: null,
            });
            // Only the DISCONNECTED case is an outage. A client that outlived the window
            // while still connected is being served by a lingering predecessor — dropping
            // the window is right, but stamping 'offline' over a live socket would be a
            // lie (and would show a false error to a user whose app works fine).
            if (!s.isSocketConnected)
              setConnectionState({
                status: "offline",
                error: "Server restarting...",
              });
          }, ttlMs);
          set({
            _handover: { signal, since: Date.now(), ttlMs, shedAt: null },
            _handoverSlowTimer: null,
            _deployTtlTimer: ttlTimer,
          });
        };

        // The hand-off's drop outlived the quiet period: now say so, in the
        // calm deploy vocabulary (the capsule, and the desk's "reconnecting").
        const handleHandoverSlow = () => {
          const s = get();
          set({ _handoverSlowTimer: null });
          if (!s._handover || s.isSocketConnected) return;
          const ms = Date.now() - (s._handover.shedAt ?? s._handover.since);
          posthog.capture(SOCKET_HANDOVER_SLOW_EVENT, {
            signal: s._handover.signal,
            ms,
          });
          console.info(
            `[Deploy] hand-off still down after ${ms}ms — showing the update capsule`
          );
          set({
            deployUpdating: {
              since: s._handover.since,
              ttlMs: s._handover.ttlMs,
            },
            isSocketReconnecting: true,
          });
        };

        // The old server's early notice: it is draining and will hand this tab
        // over (it may still be serving a turn, so the tab stays put until it
        // is let go). The wire's drain window bounds the claim.
        const handleServerShuttingDown = (
          payload?: ServerShuttingDownPayload
        ) => {
          const ttlMs = deployUpdatingTtlMs(payload?.drainingSeconds);
          armHandover("shutting_down", ttlMs);
          console.info(
            `[Deploy] update notice — hand-off window armed (ttl ${ttlMs}ms)`
          );
        };

        // The old server is letting this tab go now. The successor is already
        // live, so move at once rather than wait for the old server's close.
        const handleServerHandover = () => {
          armHandover("handover", HANDOVER_WINDOW_MS);
          console.info("[Deploy] handover — moving to the successor now");
          // A client-side close inside the window is the quiet path (above).
          if (socket.connected) socket.disconnect();
          socket.io.reconnection(true);
          socket.connect();
        };

        // AGENT_START/AGENT_END not handled here. Sandbox SOT via SANDBOX_STATE_CHANGE
        // → server → CONNECTION_STATE with displayStatus is the single mechanism.

        // Workspace sync → global toast
        const handleWorkspaceSyncStarted = (data: { reason: string }) => {
          const timer = get()._syncToastTimer;
          if (timer) clearTimeout(timer);
          set({
            workspaceSyncToast: {
              visible: true,
              status: "syncing",
              message: "Updating workspace...",
            },
            _syncToastTimer: null,
          });
        };
        const handleWorkspaceSyncCompleted = (data: {
          added: number;
          updated: number;
          unchanged: number;
        }) => {
          const total = (data.added || 0) + (data.updated || 0);
          const msg =
            total > 0
              ? `Workspace updated (${total} file${
                  total !== 1 ? "s" : ""
                } synced)`
              : "Workspace ready";
          const dismissTimer = setTimeout(() => {
            set({
              workspaceSyncToast: {
                visible: false,
                status: "syncing",
                message: "",
              },
              _syncToastTimer: null,
            });
          }, 3000);
          set({
            workspaceSyncToast: {
              visible: true,
              status: "success",
              message: msg,
            },
            _syncToastTimer: dismissTimer,
          });
        };

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("connect_error", handleConnectError);
        socket.on("server:shutting_down", handleServerShuttingDown);
        socket.on(EVENTS.SERVER_HANDOVER, handleServerHandover);
        socket.on(EVENTS.WORKSPACE_SYNC_STARTED, handleWorkspaceSyncStarted);
        socket.on(
          EVENTS.WORKSPACE_SYNC_COMPLETED,
          handleWorkspaceSyncCompleted
        );
        // Reconnect events live on the Manager (socket.io), not the Socket
        socket.io.on("reconnect_attempt", handleReconnectAttempt);
        socket.io.on("reconnect_failed", handleReconnectFailed);

        const cleanup = () => {
          const s = get();
          if (s._deployTtlTimer) clearTimeout(s._deployTtlTimer);
          if (s._deployTickTimer) clearTimeout(s._deployTickTimer);
          if (s._handoverSlowTimer) clearTimeout(s._handoverSlowTimer);
          // A pending server-close retry must die with this socket: firing later
          // would dial a torn-down socket on a stale token (two live sockets).
          if (s._serverRetryTimer) clearTimeout(s._serverRetryTimer);
          if (s._serverRetryTimer || s._serverRetries)
            set({ _serverRetryTimer: null, _serverRetries: 0 });
          clearInterval(heartbeatTimer);
          unsubscribeHealth();
          stopHealthEvaluator();
          setSocketConnected(false);
          socket.off("connect", handleConnect);
          socket.off("disconnect", handleDisconnect);
          socket.off("connect_error", handleConnectError);
          socket.off("server:shutting_down", handleServerShuttingDown);
          socket.off(EVENTS.SERVER_HANDOVER, handleServerHandover);
          usePresenceStore.getState()._unsubscribe();
          socket.off(EVENTS.WORKSPACE_SYNC_STARTED, handleWorkspaceSyncStarted);
          socket.off(
            EVENTS.WORKSPACE_SYNC_COMPLETED,
            handleWorkspaceSyncCompleted
          );
          socket.io.off("reconnect_attempt", handleReconnectAttempt);
          socket.io.off("reconnect_failed", handleReconnectFailed);
          window.removeEventListener("online", handleOnline);
          window.removeEventListener("offline", handleOffline);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
          socket.disconnect();
        };

        set({ _socket: socket, _cleanupFns: [cleanup] });

        // Register accessors so other stores can access socket without circular imports.
        // Note: getSelectedTaskId is registered separately by taskStore via registerSelectedTaskIdAccessor.
        registerSocketAccessors({
          getSocket: () => get()._socket,
          ensureConnected: () => get().ensureConnected(),
        });
      },

      destroySocket: () => {
        const { _cleanupFns } = get();
        for (const fn of _cleanupFns) fn();
        // The next socket (a login after logout) starts as a never-connected
        // page — this one's evidence must not present it as "down".
        resetHealthEvidence();
        // Clear the auth code too — a stale code from this session must not
        // mislabel the next session's escape copy.
        set({
          _socket: null,
          _cleanupFns: [],
          isSocketConnected: false,
          isSocketReconnecting: false,
          lastConnectErrorCode: null,
          deployUpdating: null,
          deployUpdatedTick: false,
          _deployTtlTimer: null,
          _deployTickTimer: null,
          _handover: null,
          _handoverSlowTimer: null,
        });
      },
    }),
    { name: "ConnectionStore", enabled: process.env.NODE_ENV === "development" }
  )
);

// An unblock re-dials: socket.io never retries a socket the server's
// middleware refused with ACCOUNT_BLOCKED, so without this the lifted gate
// opens the app on a dead link. One subscription for the store's life; with
// no socket, or a live one, ensureConnected does nothing.
onAccountUnblocked(() => useConnectionStore.getState().ensureConnected());
